import React, { useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import Button from '../../components/ui/Button';
import PhoneCodeSelect from '../../components/ui/PhoneCodeSelect';
import TurnstileWidget, { TURNSTILE_SITE_KEY, type TurnstileHandle } from '../../components/ui/Turnstile';
import { ScrollText, Reveal, Stagger, StaggerItem } from '../../components/motion';
import { submitContact } from '../../api/contact';
import { nationalDigits, phoneProblem } from '../../data/dialCodes';
import usePageMeta from '../../hooks/usePageMeta';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import Icon from '../../components/ui/Icon';
import Eyebrow from '../../components/ui/Eyebrow';
import { BRANCHES, CONTACT_EMAIL, MAIN_PHONE, cityLabel, telHref } from '../../constants/contact';
import styles from './Contact.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Customers write from Saudi Arabia, so the phone starts on +966. */
const SAUDI_CODE = '+966';

/** "What is it about?" — the chosen topic heads the message we send. */
const TOPICS = ['Get a quote', 'Track a box', 'Pickup', 'Something else'];

// Head office first, then the rest in their usual order.
const HQ_CITY = 'RIYADH';
const BRANCH_LIST = [...BRANCHES.filter((b) => b.city === HQ_CITY), ...BRANCHES.filter((b) => b.city !== HQ_CITY)];

const Contact: React.FC = () => {
  usePageMeta({
    title: 'Contact Sahari Cargo — 12 Branches Across Saudi Arabia',
    description:
      'Contact Sahari Cargo for cargo and courier from Saudi Arabia. Call your nearest branch in Riyadh, Jeddah, Makkah, Madinah and more, or send us a message.',
    url: 'https://www.saharicargo.com/contact',
  });
  const [name, setName] = useState('');
  const [phoneCode, setPhoneCode] = useState(SAUDI_CODE);
  const [topic, setTopic] = useState(TOPICS[0]);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const captchaPending = !!TURNSTILE_SITE_KEY && !captchaToken;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || captchaPending) return;
    setError(null);
    setPhoneError(null);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in your name, email and message.');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    // Phone is optional — but when a number is entered it must be valid for the
    // chosen country code (right digit count for that code).
    const national = nationalDigits(phone);
    if (national) {
      const problem = phoneProblem(phoneCode, phone);
      if (problem) {
        setPhoneError(problem);
        return;
      }
    }

    // Prefix the dial code onto the national digits, e.g. "+97150 123 4567" →
    // "+971501234567". Left empty when no number was entered.
    const fullPhone = national ? `${phoneCode}${national}` : '';

    setSubmitting(true);
    try {
      await submitContact(
        {
          name,
          email,
          phone: fullPhone,
          message: `[${topic}] ${message.trim()}`,
        },
        captchaToken,
      );
      setSent(true);
      setName('');
      setPhone('');
      setPhoneCode(SAUDI_CODE);
      setTopic(TOPICS[0]);
      setEmail('');
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your message.');
    } finally {
      setSubmitting(false);
      turnstileRef.current?.reset();
    }
  };

  return (
    <section className={styles.page}>
      <Container fluid="xl" className={styles.inner}>
        <Reveal direction="up" className={styles.split}>
          {/* Red info panel */}
          <div className={styles.info}>
            <Eyebrow light>Contact us</Eyebrow>
            <div>
              <ScrollText as="h1" className={styles.title} mode="enter">
                Talk to a Sahari Team
              </ScrollText>
              <p className={styles.description}>
                Questions about sending cargo or courier from Saudi Arabia? Call your nearest Sahari Cargo branch — we
                have 12 across the Kingdom, from Jeddah and Riyadh to Jizan and Duba — or send us a message below and
                our team will get back to you.
              </p>
            </div>
            <ul className={styles.lines}>
              <li>
                <span className={styles.lineIcon} aria-hidden="true">
                  <Icon icon={FaPhoneAlt} />
                </span>
                <span>
                  <a href={telHref(MAIN_PHONE)}>{MAIN_PHONE}</a>
                  <small>Head office · Riyadh</small>
                </span>
              </li>
              <li>
                <span className={styles.lineIcon} aria-hidden="true">
                  <Icon icon={FaEnvelope} />
                </span>
                <span>
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                  <small>Email us any time</small>
                </span>
              </li>
              <li>
                <span className={styles.lineIcon} aria-hidden="true">
                  <Icon icon={FaMapMarkerAlt} />
                </span>
                <span>
                  <b>12 branches across KSA</b>
                  <small>Drop in or call your nearest one</small>
                </span>
              </li>
            </ul>
          </div>

          {/* Form panel */}
          <Stagger as="form" className={styles.form} onSubmit={handleSubmit} stagger={0.06} delay={0.1}>
            <StaggerItem as="div">
              <h2 className={styles.formTitle}>Send us a message</h2>
            </StaggerItem>
            <StaggerItem as="div" className={styles.row}>
              <label className={styles.field}>
                <span className={styles.label}>Your name</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Full name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Email</span>
                <input
                  type="email"
                  className={styles.input}
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
            </StaggerItem>
            <StaggerItem as="div">
              <div className={styles.field}>
                <span className={styles.label}>Phone</span>
                <div className={`${styles.phoneBox} ${phoneError ? styles.phoneBoxBad : ''}`}>
                  <PhoneCodeSelect
                    value={phoneCode}
                    onChange={(code) => {
                      setPhoneCode(code);
                      setPhoneError(null);
                    }}
                    invalid={!!phoneError}
                  />
                  <input
                    type="tel"
                    inputMode="tel"
                    className={styles.phoneInput}
                    placeholder="50 123 4567"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setPhoneError(null);
                    }}
                    aria-label="Phone number"
                    aria-invalid={!!phoneError}
                  />
                </div>
                {phoneError && (
                  <p className={styles.fieldError} role="alert">
                    {phoneError}
                  </p>
                )}
              </div>
            </StaggerItem>
            <StaggerItem as="div" className={styles.field}>
              <span className={styles.label} id="contact-topic">
                What is it about?
              </span>
              <div className={styles.topics} role="radiogroup" aria-labelledby="contact-topic">
                {TOPICS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={topic === t}
                    className={`${styles.topic} ${topic === t ? styles.topicOn : ''}`}
                    onClick={() => setTopic(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </StaggerItem>
            <StaggerItem as="div">
              <label className={styles.field}>
                <span className={styles.label}>Your message</span>
                <textarea
                  className={styles.textarea}
                  placeholder="Tell us what you are sending and where…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>
            </StaggerItem>
            <StaggerItem as="div">
              <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} className={styles.captcha} />
              <Button type="submit" variant="primary" className={styles.submit} disabled={submitting || captchaPending}>
                {submitting ? 'Sending…' : 'Send message →'}
              </Button>
              {error && (
                <p className={styles.formError} role="alert">
                  {error}
                </p>
              )}
              {sent && !error && (
                <p className={styles.formSuccess} role="status">
                  Thanks — your message has been sent. We’ll get back to you shortly.
                </p>
              )}
              <p className={styles.fine}>We usually reply the same day.</p>
            </StaggerItem>
          </Stagger>
        </Reveal>

        {/* Branches */}
        <div className={styles.branches}>
          <Eyebrow>Call a branch</Eyebrow>
          <h2 className={styles.branchesTitle}>12 branches, one near you</h2>
          <ul className={styles.branchList}>
            {BRANCH_LIST.map((b) => (
              <li key={b.city} className={styles.branch}>
                <span>
                  <b>
                    {cityLabel(b.city)}
                    {b.city === HQ_CITY && <em className={styles.hq}>HQ</em>}
                  </b>
                  <small>{b.phone}</small>
                </span>
                <a
                  href={telHref(b.phone)}
                  className={styles.callBtn}
                  aria-label={`Call ${cityLabel(b.city)} branch, ${b.phone}`}
                >
                  <Icon icon={FaPhoneAlt} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
};

export default Contact;
